/**
 * soldOutSync.js
 *
 * Syncs the `soldOut` boolean flag in listing publicData after any stock change.
 *
 * WHY THIS EXISTS
 * Sharetribe's stock API only supports minStock filtering (stock >= N). It has no
 * way to query "only stock=0" listings. To support a true "Show Only Sold Out"
 * filter (pub_soldOut=true), we maintain a `soldOut` flag in publicData that mirrors
 * the listing's stock state. This flag must be kept in sync everywhere stock changes.
 *
 * USAGE
 * Call syncSoldOutFlag() after every successful sdk.stock.compareAndSet() or
 * sdk.stockAdjustments.create(). The flag update is best-effort — a failure here
 * should NOT fail the stock update itself (catch and ignore in the caller).
 *
 * soldOut: true  → listing stock is 0; appears in pub_soldOut=true filter queries
 * soldOut: false → listing has stock; excluded from sold-out filter queries
 *
 * SERVER-SIDE EQUIVALENT
 * Server-side code (e.g. initiate-privileged.js, the TradMarket API event poller)
 * uses integrationSdk.listings.update() directly since those contexts use the
 * integration SDK, not the browser SDK. The same publicData shape applies.
 */

/**
 * Updates soldOut flag in listing publicData to reflect current stock level.
 *
 * @param {Object} sdk - Sharetribe SDK instance (browser/marketplace SDK)
 * @param {Object|string} listingId - Listing UUID or SDK UUID object
 * @param {number} newStock - The new total stock value after the stock change
 * @returns {Promise}
 */
export const syncSoldOutFlag = (sdk, listingId, newStock) => {
  return sdk.ownListings.update({
    id: listingId,
    publicData: { soldOut: newStock === 0 },
  });
};
